import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Mail, MessageSquare } from 'lucide-react';
import { 
  replaceVariables, 
  generatePreviewVariables, 
  formatTimezone,
  determineEffectiveTimezone,
  isValidTimezone
} from '@/utils/templateVariables';

interface MessagePreviewPanelProps {
  subjectTemplate?: string;
  messageTemplate: string;
  selectedParticipant: any | null;
  checkIn: any;
  organizationName: string;
  senderName: string;
  isEmail?: boolean;
}

// Helper function to get participant initials
const getInitials = (name: string) => {
  return name
    .split(' ')
    .map(word => word[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

export function MessagePreviewPanel({
  subjectTemplate,
  messageTemplate,
  selectedParticipant,
  checkIn,
  organizationName,
  senderName,
  isEmail = false
}: MessagePreviewPanelProps) {
  const previewData = useMemo(() => {
    if (!selectedParticipant || !checkIn) {
      return null;
    }

    // Debug timezone determination
    const effectiveTimezone = determineEffectiveTimezone(checkIn, selectedParticipant);
    const usingParticipantTimezone = checkIn.timezone_reminder && 
                                    selectedParticipant.timezone && 
                                    isValidTimezone(selectedParticipant.timezone);

    const variables = generatePreviewVariables(
      selectedParticipant,
      checkIn,
      organizationName,
      senderName
    );

    const renderedSubject = subjectTemplate ? replaceVariables(subjectTemplate, variables) : '';
    const renderedMessage = replaceVariables(messageTemplate, variables);

    return {
      variables,
      renderedSubject,
      renderedMessage,
      effectiveTimezone,
      usingParticipantTimezone,
      participantTimezone: selectedParticipant.timezone,
      checkInTimezone: checkIn.timezone,
      timezoneReminderEnabled: checkIn.timezone_reminder
    };
  }, [subjectTemplate, messageTemplate, selectedParticipant, checkIn, organizationName, senderName]);

  if (!selectedParticipant || !previewData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            {isEmail ? (
              <Mail className="h-4 w-4" />
            ) : (
              <MessageSquare className="h-4 w-4" />
            )}
            Preview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-sm">
            Select a participant to see the preview
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {isEmail ? (
            <Mail className="h-4 w-4" />
          ) : (
            <MessageSquare className="h-4 w-4" />
          )}
          Preview
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Participant Info */}
        <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
          <Avatar className="h-8 w-8">
            <AvatarImage src={selectedParticipant.avatar_url || undefined} />
            <AvatarFallback>{getInitials(selectedParticipant.name)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <p className="font-medium text-sm">{selectedParticipant.name}</p>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>{formatTimezone(previewData.effectiveTimezone)}</span>
              {previewData.usingParticipantTimezone ? (
                <Badge variant="secondary" className="text-xs px-1.5 py-0.5">Personal TZ</Badge>
              ) : (
                <Badge variant="outline" className="text-xs px-1.5 py-0.5">Check-in TZ</Badge>
              )}
            </div>
          </div>
          <Badge variant="outline" className="text-xs">
            {isEmail ? 'Email' : 'Slack'}
          </Badge>
        </div>

        {/* Preview Content */}
        <div className="space-y-3">
          {/* Subject Line (Email only) */}
          {isEmail && subjectTemplate && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Subject:</p>
              <div className="p-3 bg-accent/20 rounded border-l-2 border-accent">
                <p className="text-sm font-medium">{previewData.renderedSubject}</p>
              </div>
            </div>
          )}

          {/* Message Body */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Message:</p>
            <div className="p-3 bg-accent/20 rounded border-l-2 border-accent">
              <div className="text-sm whitespace-pre-wrap">
                {previewData.renderedMessage}
              </div>
            </div>
          </div>
        </div>

        {/* Variable Values (for debugging) */}
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
            View variable values
          </summary>
          <div className="mt-2 space-y-3">
            {/* Timezone Debug Info */}
            <div className="p-2 bg-blue-50 dark:bg-blue-950/30 rounded text-xs space-y-1">
              <p className="font-medium text-blue-700 dark:text-blue-400">Timezone Logic:</p>
              <div className="space-y-1 text-blue-600 dark:text-blue-300">
                <p>• Timezone reminders: {previewData.timezoneReminderEnabled ? 'Enabled' : 'Disabled'}</p>
                <p>• Participant timezone: {previewData.participantTimezone || 'Not set'}</p>
                <p>• Check-in timezone: {previewData.checkInTimezone}</p>
                <p>• <strong>Using: {previewData.effectiveTimezone}</strong> ({previewData.usingParticipantTimezone ? 'participant' : 'check-in'} timezone)</p>
              </div>
            </div>
            
            {/* Variable Values */}
            <div className="p-2 bg-muted/30 rounded font-mono text-xs space-y-1">
              {Object.entries(previewData.variables).map(([key, value]) => (
                <div key={key} className="flex justify-between">
                  <span className="text-muted-foreground">{`{{${key}}}:`}</span>
                  <span className="ml-2 break-all">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </details>
      </CardContent>
    </Card>
  );
} 